<template>
  <div class="conf-page">
    <!-- ═══ APPBAR ═══ -->
    <header class="appbar">
      <router-link to="/" class="vm-mark">
        <img
          class="vm-logo"
          src="../assets/images/logo-videomedi.png"
          alt="Videomedi"
        />
      </router-link>
      <nav class="appnav">
        <router-link to="/" exact active-class="is-active">Início</router-link>
        <router-link
          :to="{ path: '/', query: { buscar: '1' } }"
          active-class="is-active"
        >
          Buscar profissionais de saúde
        </router-link>
        <router-link v-if="loggedIn" to="/consultas" active-class="is-active">
          Minhas consultas
        </router-link>
        <router-link v-if="loggedIn" to="/documentos" active-class="is-active">
          Documentos
        </router-link>
        <router-link
          v-if="loggedIn"
          to="/meus-pagamentos"
          active-class="is-active"
        >
          Pagamentos
        </router-link>
      </nav>
      <span class="appbar-spacer" />
      <AppBarUserMenu v-if="loggedIn" />
      <div v-else class="appbar-auth">
        <router-link to="/login" class="btn btn--outline btn--sm">
          Entrar
        </router-link>
        <router-link to="/cadastrar" class="btn btn--primary btn--sm">
          Criar conta
        </router-link>
      </div>
    </header>

    <!-- ═══ WIZARD — todos concluídos ═══ -->
    <div class="wizard-row">
      <div class="steps">
        <div class="step step--done">
          <span class="step-dot">✓</span>
          <span class="step-label">Profissional</span>
        </div>
        <span class="step-bar step-bar--done" />
        <template v-if="!isExpressAppointment">
          <div class="step step--done">
            <span class="step-dot">✓</span>
            <span class="step-label">Horário</span>
          </div>
          <span class="step-bar step-bar--done" />
        </template>
        <div class="step step--done">
          <span class="step-dot">✓</span>
          <span class="step-label">Pagamento</span>
        </div>
        <span
          class="step-bar"
          :class="{ 'step-bar--done': !isPix || pixConfirmed }"
        />
        <div class="step" :class="{ 'step--done': !isPix || pixConfirmed }">
          <span class="step-dot">
            {{
              !isPix || pixConfirmed ? "✓" : isExpressAppointment ? "3" : "4"
            }}
          </span>
          <span class="step-label">Confirmação</span>
        </div>
      </div>
    </div>

    <!-- ═══ HERO ═══ -->
    <div class="confirm-hero">
      <div class="ring" :class="isPix && !pixConfirmed ? 'ring--warning' : ''">
        <svg
          width="40"
          height="40"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M20 6L9 17l-5-5"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </div>
      <h1>{{ heroTitle }}</h1>
      <p>{{ heroText }}</p>
    </div>

    <!-- ═══ PIX PENDENTE ═══ -->
    <div
      v-if="isPix && paymentStatus === 'expired'"
      class="checkout alert-warning"
      style="padding-bottom:0"
    >
      <p>{{ paymentMessage || "O prazo de pagamento expirou." }}</p>
    </div>
    <div
      v-if="
        isPix &&
          !pixConfirmed &&
          paymentStatus !== 'expired' &&
          (pixPaymentData || pixData)
      "
      class="checkout"
      style="padding-bottom:0"
    >
      <div style="max-width:480px;margin:0 auto">
        <Pix :pix-data="pixPaymentData || pixData" />
      </div>
    </div>

    <!-- ═══ CORPO (pós-confirmação) ═══ -->
    <div v-if="!isPix || pixConfirmed" class="checkout">
      <div class="checkout-grid">
        <!-- DETALHES -->
        <div class="stack" style="gap:var(--space-6)">
          <!-- Appointment card -->
          <div class="card card--success appt-card">
            <div class="when">
              <div class="d">{{ apptDayLabel }}</div>
              <div class="h">{{ apptTime }}</div>
            </div>
            <div class="stack" style="gap:var(--space-2);flex:1">
              <div class="row" style="gap:var(--space-3)">
                <div class="avatar avatar--md avatar--ring">
                  {{ doctorInitials }}
                </div>
                <div class="stack" style="gap:1px;flex:1">
                  <span
                    style="font-weight:var(--fw-bold);color:var(--color-gray-900);font-size:var(--font-size-md)"
                  >
                    {{ doctorName }}
                  </span>
                  <span
                    style="font-size:var(--font-size-sm);color:var(--color-gray-500)"
                  >
                    {{ doctorSpecialty }} · {{ doctorCRM }} · por vídeo
                  </span>
                </div>
                <span class="badge badge--success badge--no-dot">
                  Confirmada
                </span>
              </div>
              <div class="appt-actions">
                <button
                  class="btn btn--outline btn--sm"
                  @click="$router.push({ name: 'Consultas' })"
                >
                  Minhas consultas
                </button>
                <button
                  class="btn btn--ghost btn--sm"
                  @click="$router.push({ name: 'Home', query: { buscar: 1 } })"
                >
                  Agendar outra consulta
                </button>
              </div>
            </div>
          </div>

          <!-- Lembrete -->
          <div
            class="card"
            style="padding:var(--space-4) var(--space-5);display:flex;align-items:center;gap:var(--space-3)"
          >
            <div
              class="avatar avatar--sm"
              style="background:var(--color-primary-light);color:var(--color-primary)"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M10.3 21a1.94 1.94 0 0 0 3.4 0"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                />
              </svg>
            </div>
            <div class="stack" style="gap:0;flex:1">
              <span
                style="font-size:var(--font-size-sm);font-weight:var(--fw-semibold);color:var(--color-gray-900)"
              >
                Lembretes ativados
              </span>
              <span
                style="font-size:var(--font-size-xs);color:var(--color-gray-500)"
              >
                Avisaremos 24h e 1h antes — por push, e-mail e SMS
              </span>
            </div>
          </div>

          <!-- Como se preparar -->
          <div class="card" style="padding:var(--space-6)">
            <h2
              style="font-size:var(--font-size-lg);font-weight:var(--fw-bold);color:var(--color-gray-900);margin:0 0 var(--space-4)"
            >
              Como se preparar
            </h2>
            <div class="prep-list">
              <div class="it">
                <span class="ck">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M20 6L9 17l-5-5"
                      stroke="currentColor"
                      stroke-width="3"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </span>
                Tenha um documento com foto em mãos
              </div>
              <div class="it">
                <span class="ck">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M20 6L9 17l-5-5"
                      stroke="currentColor"
                      stroke-width="3"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </span>
                Liste seus sintomas e medicações em uso
              </div>
              <div class="it">
                <span class="ck">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M20 6L9 17l-5-5"
                      stroke="currentColor"
                      stroke-width="3"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </span>
                Escolha um ambiente silencioso com boa conexão
              </div>
              <div class="it">
                <span class="ck">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M20 6L9 17l-5-5"
                      stroke="currentColor"
                      stroke-width="3"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </span>
                Teste sua câmera e microfone antes de entrar
              </div>
            </div>
          </div>
        </div>

        <!-- SALA DE ATENDIMENTO -->
        <aside class="card room-card">
          <div class="stack" style="gap:var(--space-1)">
            <span
              style="font-size:var(--font-size-md);font-weight:var(--fw-bold);color:var(--color-gray-900)"
            >
              Sua sala de atendimento
            </span>
            <span
              style="font-size:var(--font-size-sm);color:var(--color-gray-500)"
            >
              Seu atendimento poderá ser realizado por aqui ou pelo aplicativo
              Videomedi.
            </span>
          </div>
          <div class="summary-row">
            <span class="k">Abre</span>
            <span class="v hl">10 min antes</span>
          </div>
          <!-- <router-link
            v-if="appointmentRoomId"
            :to="{ name: 'WaitingRoom', params: { room: appointmentRoomId } }"
            class="btn btn--accent btn--lg btn--block"
          >
            Entrar na sala
          </router-link> -->
          <p
            style="font-size:var(--font-size-xs);color:var(--color-gray-500);text-align:center"
          >
            Disponível a partir das {{ roomOpensAt }}
          </p>
          <div class="book-trust">
            <span class="t">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <rect
                  x="2"
                  y="6"
                  width="14"
                  height="12"
                  rx="2"
                  stroke="currentColor"
                  stroke-width="2"
                />
                <path
                  d="M16 10l6-3v10l-6-3"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linejoin="round"
                />
              </svg>
              Consulta por vídeo · 30 min
            </span>
            <span class="t">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linejoin="round"
                />
                <path
                  d="M14 2v6h6"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linejoin="round"
                />
              </svg>
              Receita e atestado ao final, se necessário
            </span>
          </div>
        </aside>
      </div>
    </div>

    <!-- ═══ RODAPÉ ═══ -->
    <footer class="foot-mini">
      <span class="vm-mark">
        <img
          class="vm-logo vm-logo--sm"
          src="../assets/images/logo-videomedi.png"
          alt="Videomedi"
        />
      </span>
      <span class="cpy">© 2026 Videomedi · CNPJ 00.000.000/0001-00</span>
      <span class="links">
        <a href="#">Privacidade</a>
        <a href="#">Termos de Uso</a>
        <a href="#">LGPD</a>
        <a href="#">Suporte</a>
      </span>
    </footer>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import Pix from "./../components/Pix.vue";
import AppBarUserMenu from "./../components/AppBarUserMenu.vue";
import { isImmediateConsultation } from "./../utils/appointments/immediateConsultation";

export default {
  name: "SchedulePaymentConfirmationView",
  components: { Pix, AppBarUserMenu },

  data() {
    return {
      paymentStatus: null,
      pixPaymentData: null,
      paymentMessage: null
    };
  },

  beforeDestroy() {
    this.$store.dispatch("appointments/clearScheduleAppointment");
  },

  async mounted() {
    if (this.isPix) {
      const appointmentId =
        this.$route.params.appointmentResponse &&
        this.$route.params.appointmentResponse.id;
      if (!appointmentId) {
        this.paymentStatus = "paid";
        return;
      }
      try {
        const { data } = await this.fetchPendingPayment(appointmentId);
        this.pixPaymentData = data.order.pix_data;
        this.paymentStatus = "ready";
      } catch (err) {
        const errorType =
          err &&
          err.response &&
          err.response.data &&
          err.response.data.error_type;
        if (errorType === "payment_already_paid") {
          this.paymentStatus = "paid";
        } else if (errorType === "payment_expired") {
          this.paymentStatus = "expired";
          this.paymentMessage =
            err.response.data.errors && err.response.data.errors.details;
        }
      }
    }
  },

  methods: {
    ...mapActions("appointments", ["fetchPendingPayment"])
  },

  computed: {
    ...mapGetters("auth", ["loggedIn"]),
    ...mapGetters("user", ["getCurrentUser"]),
    ...mapGetters("appointments", ["currentSchedule"]),

    isExpressAppointment() {
      return isImmediateConsultation(this.currentSchedule);
    },
    isPix() {
      return this.$route.params.payment_method === "pix";
    },
    pixData() {
      const resp = this.$route.params.appointmentResponse;
      if (!resp) return null;
      if (resp.appointments_orders && resp.appointments_orders[0])
        return resp.appointments_orders[0].pix_data || null;
      return (resp.order && resp.order.pix_data) || null;
    },
    pixConfirmed() {
      return this.isPix && this.paymentStatus === "paid";
    },
    heroTitle() {
      if (!this.isPix || this.pixConfirmed) return "Consulta confirmada!";
      return "Agendamento pendente de pagamento!";
    },
    heroText() {
      if (!this.isPix || this.pixConfirmed)
        return "Pagamento aprovado e horário garantido. Enviamos os detalhes para o seu e-mail.";
      return "Não se preocupe, o agendamento foi criado mas ainda falta a etapa do pagamento.";
    },

    doctor() {
      return (
        this.currentSchedule?.doctor ||
        this.$route.params.appointmentResponse?.doctor ||
        {}
      );
    },
    doctorInitials() {
      return (
        (this.doctor.name || "")
          .split(" ")
          .slice(0, 2)
          .map(n => n[0])
          .join("")
          .toUpperCase() || ""
      );
    },
    doctorName() {
      return (
        this.doctor.name ||
        this.$route.params.appointmentResponse?.doctor_name ||
        ""
      );
    },
    doctorSpecialty() {
      return (
        this.doctor.doctor_property?.medical_specialty_name ||
        this.$route.params.appointmentResponse?.doctor_specialty ||
        ""
      );
    },
    doctorCRM() {
      return this.doctor.doctor_property?.council_description || "";
    },

    scheduledAt() {
      return (
        this.currentSchedule?.scheduled_at ||
        this.$route.params.appointmentResponse?.scheduled_at ||
        ""
      );
    },
    apptDayLabel() {
      if (!this.scheduledAt) return "";
      try {
        const d = new Date(this.scheduledAt);
        const wd = d
          .toLocaleDateString("pt-BR", { weekday: "short" })
          .replace(".", "");
        const day = d
          .toLocaleDateString("pt-BR", {
            day: "numeric",
            month: "short",
            year: "numeric"
          })
          .replace(/ de /g, " ");
        return `${wd} · ${day}`;
      } catch {
        return "—";
      }
    },
    apptTime() {
      if (!this.scheduledAt) return "";
      try {
        return new Date(this.scheduledAt).toLocaleTimeString("pt-BR", {
          hour: "2-digit",
          minute: "2-digit"
        });
      } catch {
        return "—";
      }
    },
    appointmentRoomId() {
      const resp = this.$route.params.appointmentResponse;
      if (!resp) return null;
      return resp.room_id || null;
    },
    roomOpensAt() {
      if (!this.scheduledAt) return "";
      try {
        const d = new Date(this.scheduledAt);
        d.setMinutes(d.getMinutes() - 10);
        return d.toLocaleTimeString("pt-BR", {
          hour: "2-digit",
          minute: "2-digit"
        });
      } catch {
        return "—";
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.vm-logo {
  display: block;
  height: 28px;
  width: auto;
}

.vm-logo--sm {
  height: 22px;
}

/* ─── PAGE ─── */
.conf-page {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: var(--color-bg-page, #f4f6fb);
}

/* ─── APPBAR ─── */
.appbar {
  display: flex;
  align-items: center;
  gap: var(--space-8);
  background: #fff;
  border-bottom: 1px solid var(--color-gray-200);
  padding: 0 var(--space-8);
  height: 68px;
  position: sticky;
  top: 0;
  z-index: 10;
}

.vm-mark {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  text-decoration: none;
}

.vm-wordmark {
  font-size: var(--font-size-md);
  font-weight: var(--fw-extra);
  color: var(--color-accent);
  letter-spacing: -0.01em;
}

.appnav {
  display: flex;
  align-items: center;
  gap: var(--space-6);

  a {
    font-size: var(--font-size-sm);
    font-weight: var(--fw-semibold);
    color: var(--color-gray-600);
    text-decoration: none;
    cursor: pointer;
    &:hover {
      color: var(--color-primary);
    }
    &.is-active,
    &.router-link-active {
      color: var(--color-accent);
    }
  }
}

.appbar-spacer {
  flex: 1;
}

.appbar-auth {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.appbar-user {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  .nm {
    font-size: var(--font-size-sm);
    font-weight: var(--fw-bold);
    color: var(--color-gray-800);
  }
}

/* ─── WIZARD ─── */
.wizard-row {
  display: flex;
  justify-content: center;
  padding: var(--space-6) var(--space-10) 0;
}

.steps {
  display: flex;
  align-items: center;
  max-width: 620px;
  width: 100%;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-1);

  .step-dot {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: 2px solid var(--color-gray-300);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: var(--font-size-xs);
    font-weight: var(--fw-bold);
    color: var(--color-gray-400);
    background: #fff;
  }

  .step-label {
    font-size: var(--font-size-xs);
    font-weight: var(--fw-semibold);
    color: var(--color-gray-400);
    white-space: nowrap;
  }

  &.step--done .step-dot {
    background: var(--color-success);
    border-color: var(--color-success);
    color: #fff;
  }
  &.step--done .step-label {
    color: var(--color-gray-700);
  }
}

.step-bar {
  flex: 1;
  height: 2px;
  background: var(--color-gray-200);
  margin: 0 var(--space-1);
  margin-top: -16px;

  &.step-bar--done {
    background: var(--color-success);
  }
}

/* ─── CONFIRM HERO ─── */
.confirm-hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: var(--space-3);
  padding: var(--space-10) var(--space-6) var(--space-2);

  .ring {
    width: 84px;
    height: 84px;
    border-radius: 50%;
    background: var(--color-success-light);
    color: var(--color-success);
    display: flex;
    align-items: center;
    justify-content: center;

    &.ring--warning {
      background: var(--color-warning-light);
      color: var(--color-warning);
    }
  }

  h1 {
    font-size: var(--font-size-3xl);
    font-weight: var(--fw-extra);
    color: var(--color-gray-900);
    letter-spacing: -0.02em;
    margin: 0;
  }

  p {
    font-size: var(--font-size-md);
    color: var(--color-gray-500);
    margin: 0;
    max-width: 480px;
  }
}

/* ─── CHECKOUT WRAPPER ─── */
.checkout {
  max-width: 960px;
  margin: 0 auto;
  padding: var(--space-8) var(--space-10) var(--space-12);
  width: 100%;

  @media (max-width: 860px) {
    padding: var(--space-5);
  }
}

.checkout-grid {
  display: grid;
  grid-template-columns: 1fr 348px;
  gap: var(--space-8);
  align-items: start;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
}

/* ─── CARD padding ausente no global ─── */
.card {
  padding: var(--space-6);
}

/* ─── APPT CARD ─── */
.appt-card {
  display: flex;
  align-items: center;
  gap: var(--space-5);
  padding: var(--space-6);

  .when {
    text-align: center;
    padding: var(--space-3) var(--space-5);
    background: var(--color-success-light);
    border-radius: var(--radius-lg);
    flex: none;

    .d {
      font-size: var(--font-size-xs);
      font-weight: var(--fw-bold);
      color: var(--color-success);
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }
    .h {
      font-family: var(--font-num, "Inter", sans-serif);
      font-size: var(--font-size-2xl);
      font-weight: var(--fw-bold);
      color: var(--color-success);
    }
  }
}

.appt-actions {
  display: flex;
  gap: var(--space-2);
  flex-wrap: wrap;
  margin-top: var(--space-3);
}

/* ─── PREP LIST ─── */
.prep-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);

  .it {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    font-size: var(--font-size-sm);
    color: var(--color-gray-600);
  }

  .ck {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: var(--color-success-light);
    color: var(--color-success);
    display: flex;
    align-items: center;
    justify-content: center;
    flex: none;
  }
}

/* ─── ROOM CARD ─── */
.room-card {
  position: sticky;
  top: var(--space-4);
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  padding: var(--space-6);
  border-color: var(--color-accent) !important;

  @media (max-width: 860px) {
    position: static;
  }
}

.summary-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: var(--font-size-sm);

  .k {
    color: var(--color-gray-500);
  }
  .v {
    font-weight: var(--fw-bold);
    color: var(--color-gray-900);
  }
  .v.hl {
    color: var(--color-success);
  }
}

/* ─── BOOK TRUST ─── */
.book-trust {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding-top: var(--space-2);
  border-top: 1px solid var(--color-gray-100);

  .t {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    font-size: var(--font-size-xs);
    color: var(--color-gray-500);
    svg {
      color: var(--color-success);
      flex: none;
    }
  }
}

/* ─── FOOT MINI ─── */
.foot-mini {
  display: flex;
  align-items: center;
  gap: var(--space-5);
  padding: var(--space-5) var(--space-8);
  border-top: 1px solid var(--color-gray-200);
  background: var(--color-bg-page, #f4f6fb);
  margin-top: auto;

  .vm-mark {
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
  }
  .vm-wordmark {
    color: var(--color-gray-500);
    font-size: var(--font-size-sm);
    font-weight: normal;
  }
  .cpy {
    font-size: var(--font-size-xs);
    color: var(--color-gray-400);
  }
  .links {
    margin-left: auto;
    display: flex;
    gap: var(--space-5);
    a {
      font-size: var(--font-size-xs);
      font-weight: var(--fw-semibold);
      color: var(--color-gray-500);
      text-decoration: none;
      &:hover {
        color: var(--color-primary);
      }
    }
  }
}
</style>
